import { navigateToUrl } from '@frontend/auth';
import appConfig from '@frontend/env';
import { hydraShellService } from '../services';

export function shouldRedirectToSSO(ssoTenant: string): boolean {
  const showAuthTenantSSO = appConfig.SHOW_AUTH_TENANT_SSO === 'true';
  const currentLoginState = hydraShellService.getCurrentState();
  return showAuthTenantSSO && !!ssoTenant && (currentLoginState === 'login' || currentLoginState === 'none');
}

// Exact-match hostnames allowed for `weave://` deep links. Subdomains under `.f.weavedev.net`
// (preview/feature envs like `weave.f.weavedev.net` or `<app>-<pr>-dev.f.weavedev.net`) are also
// allowed via the suffix check below — this matches the shell's allowlist in
// apps/shell/src/utils/securityAllowlistMiddleware.ts.
const NATIVE_DEEP_LINK_HOSTS = new Set(['app.getweave.com', 'app.weavedev.net', 'localhost']);
const NATIVE_DEEP_LINK_HOST_SUFFIX = '.f.weavedev.net';

/**
 * True iff `url` is a `weave://` deep link to a known Weave host. Anything else
 * (https URLs, malformed input, weave:// to an unrecognized host) is rejected.
 */
export function isNativeDeepLink(url: string): boolean {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'weave:') return false;
    // Use `hostname` (not `host`) so a port doesn't break allowlisting.
    const hostname = parsed.hostname;
    return NATIVE_DEEP_LINK_HOSTS.has(hostname) || hostname.endsWith(NATIVE_DEEP_LINK_HOST_SUFFIX);
  } catch {
    return false;
  }
}

type NavigateOpts = Parameters<typeof navigateToUrl>[1];
// TanStack's UseNavigateResult is heavily generic over the source path; accept any callable that
// takes a NavigateOptions-shaped argument. Looser than the upstream type, but avoids forcing every
// caller's UseNavigateResult<TFrom> through a single concrete TFrom.
type RouterNavigate = (options: any) => unknown;

/**
 * Post-auth redirect dispatcher. For `weave://` URLs (the desktop shell handoff),
 * routes to /native-redirect with the URL in TanStack history state so the user
 * sees a "you can close this window" screen instead of being stranded on the
 * spinner. For anything else, falls through to navigateToUrl.
 */
export function handlePostAuthRedirect(redirectTo: string, routerNavigate: RouterNavigate, opts?: NavigateOpts): void {
  if (isNativeDeepLink(redirectTo)) {
    routerNavigate({ to: '/native-redirect', state: { redirectTo } });
    return;
  }
  navigateToUrl(redirectTo, opts);
}
